import { HttpErrorResponse } from "@angular/common/http";
import { AfterViewInit, Component, Input, OnDestroy, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import notify from "devextreme/ui/notify";
import { forkJoin } from "rxjs";
import { IOcbLocalAgent, IOcbLocalAgentCommand, IOcbRemotePlatform, LocalAgentsService, OcbLocalAgentCommandStatus, RemotePlatformsService } from "../../core";
import { LocalAgentCommandComponent } from "../local-agent-command-popup/local-agent-command-popup.component";

@Component({
    selector: 'app-local-agent-check-connection',
    templateUrl: './local-agent-check-connection-popup.component.html',
    styleUrls: ['./local-agent-check-connection-popup.component.scss']
})
export class LocalAgentCheckConnectionComponent implements AfterViewInit, OnDestroy {
    @Input() public agentId?: string;
    @Input() public remotePlatformId?: string;
    @Input() public sftpEngine: string = 'ipworks';
    @Input() public allowChangeAgent: boolean = false;

    public visible: boolean = false;

    @ViewChild('agentCommandPopup')
    public agentCommandPopup: LocalAgentCommandComponent;

    private agents: IOcbLocalAgent[];
    private remotePlatforms: IOcbRemotePlatform[];
    private remotePlatformDirectories: string[] = [];

    private canSelectAgent: boolean = true;
    private canSelectRemotePlatform: boolean = true;
    private checkProgression: string = '';
    private checkStep: number = 0;
    private currentEnvironmentId?: string;
    private directoryPath?: string;
    private isCheckInProgress: boolean = false;
    private isProgressBarVisible: boolean = false;
    private selectedAgent?: IOcbLocalAgent;
    private selectedRemotePlatform?: IOcbRemotePlatform;
    private statusLabels: any;

    private commandCheckTimer;
    private hideCheckProgressionTimer;

    public sftpEngines: Array<any> = [
        {
            id: 'ipworks',
            name: 'IPWorks SFTP'
        },
        {
            id: 'secureblackbox',
            name: 'SecureBlackbox'
        }
    ];

    constructor(private agentsService: LocalAgentsService, private remotePlatformsService: RemotePlatformsService, private route: ActivatedRoute, private translateService: TranslateService) { }

    public ngAfterViewInit() {
        this.route.params.subscribe(_ => {
            this.currentEnvironmentId = this.route.snapshot.params.id;
        });
        this.translateService.get('localAgentsCommandsStatus').subscribe(labels => {
            this.statusLabels = labels;
        });
    }

    public ngOnDestroy() {
        if (this.commandCheckTimer) clearTimeout(this.commandCheckTimer);
        if (this.hideCheckProgressionTimer) clearTimeout(this.hideCheckProgressionTimer);
    }

    public onHidden() {
        this.selectedAgent = null;
        this.selectedRemotePlatform = null;
        this.directoryPath = null;
        if (this.commandCheckTimer) clearTimeout(this.commandCheckTimer);
        if (this.hideCheckProgressionTimer) clearTimeout(this.hideCheckProgressionTimer);
    }

    public onShown() {
        this.isProgressBarVisible = true;
        forkJoin([
            this.agentsService.getLocalAgents(this.currentEnvironmentId, false),
            this.remotePlatformsService.getRemotePlatforms(this.currentEnvironmentId, null, false)
        ])
            .subscribe({
                next: ([agents, remotePlatforms]) => {
                    this.agents = agents;
                    this.remotePlatforms = remotePlatforms;
                    if (this.agentId) {
                        this.selectedAgent = this.agents.find(a => a.id == this.agentId);
                        this.canSelectAgent = this.allowChangeAgent;
                    }
                    else this.canSelectAgent = true;
                    if (this.remotePlatformId) {
                        this.selectedRemotePlatform = this.remotePlatforms.find(a => a.id == this.remotePlatformId);
                        this.canSelectRemotePlatform = false;
                    }
                    else this.canSelectRemotePlatform = true;
                },
                error: (err) => this.handleError(err),
                complete: () => this.isProgressBarVisible = false
            })
    }

    public checkConnection() {
        this.isProgressBarVisible = true;
        this.checkStep = 0;
        const engine = this.selectedRemotePlatform && this.selectedRemotePlatform.isSFTP ? this.sftpEngine : '';
        this.agentsService.checkConnection(this.selectedAgent.id, this.directoryPath, this.selectedRemotePlatform?.id, false, engine).subscribe({
            next: (command) => {
                this.isCheckInProgress = true;
                this.checkCommandProgression(command);
            },
            error: (error) => this.handleError(error),
            complete: () => this.isProgressBarVisible = false
        });
    }

    public onSelectedRemotePlatformChange(e) { this.loadDirectories(e.selectedItem?.id); }


    private checkCommandProgression(command: IOcbLocalAgentCommand) {
        this.checkProgression = this.statusLabels[OcbLocalAgentCommandStatus[command.status]];

        if (command.status == OcbLocalAgentCommandStatus.Running) {
            this.checkStep = 2;
            this.commandCheckTimer = setTimeout(() => this.getAndCheckCommand(command.localAgent.id, command.id), 3000);
        }
        else if (command.status == OcbLocalAgentCommandStatus.Done) {
            this.checkStep = 3;
            forkJoin([
                this.translateService.get('localAgents.msgCheckConnectionCompleted')
            ])
                .subscribe({
                    next: (messages) => {
                        notify(messages[0], 'success', 8000);
                        this.hideCheckProgressionTimer = setTimeout(() => this.hideCheckProgression(), 2000);
                        this.openCommandDetails(command);
                    },
                    error: (error) => this.handleError(error)
                });
        }
        else if (command.status == OcbLocalAgentCommandStatus.Error) {
            this.checkStep = 3;
            this.translateService.get('localAgents.msgCheckConnectionCompletedWithError').subscribe({
                next: (message) => {
                    notify(message, 'error', 8000);
                    this.hideCheckProgressionTimer = setTimeout(() => this.hideCheckProgression(), 2000);
                    this.openCommandDetails(command);
                },
                error: (error) => this.handleError(error)
            });
        }
        else if (command.status == OcbLocalAgentCommandStatus.Expired) {
            this.checkStep = 3;
            this.translateService.get('localAgents.msgCheckConnectionExpired').subscribe({
                next: (message) => {
                    notify(message, 'error', 8000);
                    this.hideCheckProgressionTimer = setTimeout(() => this.hideCheckProgression(), 2000);
                    this.openCommandDetails(command);
                },
                error: (error) => this.handleError(error)
            });
        }
        else {
            //status: Waiting
            this.checkStep = 1;
            this.commandCheckTimer = setTimeout(() => this.getAndCheckCommand(command.localAgent.id, command.id), 3000);
        }
    }

    private getAndCheckCommand(agentId, commandId) {
        this.agentsService.getCommand(agentId, commandId).subscribe({
            next: (command) => this.checkCommandProgression(command),
            error: (error) => {
                this.handleError(error);
                this.isCheckInProgress = false;
                this.checkProgression = '';
            }
        });
    }

    private hideCheckProgression() {
        this.isCheckInProgress = false;
        this.checkProgression = '';
    }

    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            } else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            } else {
                message = error.error;
            }
        } else {
            message = error;
        }

        notify(message, 'error', 3000);
    }

    private loadDirectories(remotePlatformId) {
        this.remotePlatformDirectories = [];
        if (!remotePlatformId) return;
        this.remotePlatformsService.getDirectories(remotePlatformId).subscribe((directories) => this.remotePlatformDirectories = directories);
    }

    private openCommandDetails(command: IOcbLocalAgentCommand) {
        this.agentCommandPopup.agentCommand = command;
        this.agentCommandPopup.visible = true;
    }
}